import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Input from './ui/Input';
import { chatService } from '../services/chatService';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const ChatAssistant = ({ context = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [suggestedReplies, setSuggestedReplies] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { userProfile } = useAuth();
  const location = useLocation();

  // Initialize chat session when opened
  useEffect(() => {
    if (isOpen && messages?.length === 0) {
      initializeChatSession();
    }
  }, [isOpen, userProfile, context]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  const initializeChatSession = () => {
    const pageContext = getPageContext();
    const userName = userProfile?.full_name?.split(' ')?.[0];
    
    // Show greeting
    const greeting = chatService?.getContextualGreeting({
      page: pageContext,
      userName: userName,
      hasBooking: context?.hasBooking || false
    });
    
    const initialMessage = {
      id: Date.now(),
      role: 'assistant',
      content: greeting,
      timestamp: new Date()
    };
    
    setMessages([initialMessage]);
    setSuggestedReplies(chatService?.getSuggestedReplies([]));
  };

  const getPageContext = () => {
    const path = location?.pathname;
    if (path === '/' || path?.includes('about')) return 'home';
    if (path?.includes('services')) return 'services';
    if (path?.includes('ai-solutions')) return 'ai-solutions';
    if (path?.includes('post-booking')) return 'post-booking';
    if (path?.includes('knowledge-hub')) return 'knowledge-hub';
    if (path?.includes('assessment') || path?.includes('diagnostic') || path?.includes('free-assess')) return 'assessments';
    if (path?.includes('roi-calculator')) return 'roi';
    return 'general';
  };

  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText?.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: messageText?.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await chatService?.sendMessage(
        messageText?.trim(),
        conversationHistory
      );

      if (response?.success) {
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: response?.message,
          timestamp: new Date()
        };

        setMessages(prev => [...prev, assistantMessage]);
        setConversationHistory(response?.conversationHistory);
        setSuggestedReplies(chatService?.getSuggestedReplies(response?.conversationHistory));
      } else {
        const errorMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: `I apologize, but I'm having trouble connecting right now. ${response?.error || 'Please try again in a moment.'}`,
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: "I'm experiencing technical difficulties. Please try again or contact us directly at info@northstaranalytics.eu",
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestedReply = (reply) => {
    handleSendMessage(reply);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleNewConversation = () => {
    // Reset state
    setMessages([]);
    setConversationHistory([]);
    setSuggestedReplies([]);
    setInputMessage('');
    
    // Initialize new session
    initializeChatSession();
  };

  const handleCloseChat = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gradient-to-br from-[#1FB5A5] to-[#002B45] rounded-full shadow-premium hover:shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
          aria-label="Open chat assistant"
        >
          <Icon name="MessageCircle" size={28} className="text-white" />
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1FB5A5] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#1FB5A5]"></span>
          </span>
          
          {/* Tooltip */}
          <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
            Ask about data strategy
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40 md:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Chat Panel */}
          <div className="fixed bottom-6 right-6 z-50 w-full md:w-96 h-[600px] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden md:max-w-md mx-4 md:mx-0">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#002B45] to-[#003D5C] p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#1FB5A5] rounded-full flex items-center justify-center">
                  <Icon name="Sparkles" size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Data Strategy Assistant</h3>
                  <p className="text-white/70 text-xs">Powered by AI</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleNewConversation}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="New conversation"
                  title="Start new conversation"
                >
                  <Icon name="RotateCcw" size={18} className="text-white" />
                </button>
                <button
                  onClick={handleCloseChat}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Close chat"
                >
                  <Icon name="X" size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages?.map((message) => (
                <div
                  key={message?.id}
                  className={`flex ${message?.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      message?.role === 'user' ?'bg-[#1FB5A5] text-white rounded-br-sm'
                        : message?.isError
                        ? 'bg-red-50 text-red-900 border border-red-200 rounded-bl-sm' :'bg-white text-gray-900 shadow-sm rounded-bl-sm'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message?.content}</p>
                    <p
                      className={`text-xs mt-1 ${
                        message?.role === 'user' ? 'text-white/70' : 'text-gray-500'
                      }`}
                    >
                      {formatTime(message?.timestamp)}
                    </p>
                  </div>
                </div>
              ))}

              {/* Loading Indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Suggested Replies */}
              {!isLoading && suggestedReplies?.length > 0 && messages?.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggestedReplies?.map((reply, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestedReply(reply)}
                      className="px-3 py-1.5 text-xs font-medium text-[#002B45] bg-white border border-[#1FB5A5]/30 rounded-full hover:bg-[#1FB5A5]/10 hover:border-[#1FB5A5] transition-colors"
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 bg-white border-t border-gray-200">
              <div className="flex items-end space-x-2">
                <div className="flex-1">
                  <textarea
                    ref={inputRef}
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e?.target?.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about data strategy..."
                    rows={1}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1FB5A5] focus:border-transparent resize-none text-sm"
                    disabled={isLoading}
                  />
                </div>
                <Button
                  onClick={() => handleSendMessage()}
                  disabled={!inputMessage?.trim() || isLoading}
                  className="bg-[#1FB5A5] hover:bg-[#1FB5A5]/90 text-white rounded-xl px-4 py-2 h-10"
                  iconName="Send"
                  size="icon"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                AI assistant • Responses may vary
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ChatAssistant;